import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import PageTitle from "../../layouts/PageTitle";
import { useGetProjectImprestExpensesQuery } from "../../../store/appApi";
import { selectUserRole } from "../../../store/authSlice";
import { formatCurrency, formatDateLabel, formatRoleLabel } from "./imprestConfig";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";

const ProjectExpensesReport = () => {
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);
  const { data, isLoading, isFetching } = useGetProjectImprestExpensesQuery(
    undefined,
    { skip: role !== "super-admin" },
  );

  if (role !== "super-admin") {
    return (
      <>
        <PageTitle activeMenu="Expenses Report" motherMenu="Imprest" />
        <div className="rounded-3xl border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-xl shadow-slate-200/70">
          Only a super-admin can view the imprest expenses report.
        </div>
      </>
    );
  }

  const expenses = Array.isArray(data?.expenses) ? data.expenses : [];
  const loading = isLoading || isFetching;
  const total = expenses.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0);

  return (
    <>
      <PageTitle activeMenu="Expenses Report" motherMenu="Imprest" />

      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Imprest
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900">
                Expenses Report
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-500">
                Every petty-cash expense every employee has booked against
                their imprest on this project. These do not appear as
                separate Daybook entries — the Daybook was only debited once,
                at allotment time.
              </p>
            </div>
            <button
              type="button"
              className="rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700"
              onClick={() => navigate("/imprest/admin")}
            >
              Back to Employees
            </button>
          </div>

          <div className="mt-5 max-w-xs rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs text-gray-600">
            <div className="text-gray-400">Total Expensed</div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {formatCurrency(total)}
            </div>
          </div>
        </section>

        <section className={panelClass}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-5 py-2.5 text-left font-medium">Date</th>
                  <th className="px-5 py-2.5 text-left font-medium">Employee</th>
                  <th className="px-5 py-2.5 text-left font-medium">Role</th>
                  <th className="px-5 py-2.5 text-right font-medium">Amount</th>
                  <th className="px-5 py-2.5 text-left font-medium">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-gray-500">
                      Loading expenses...
                    </td>
                  </tr>
                )}

                {!loading && expenses.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-gray-500">
                      No imprest expenses booked yet.
                    </td>
                  </tr>
                )}

                {!loading &&
                  expenses.map((entry) => (
                    <tr key={entry._id}>
                      <td className="px-5 py-2.5 text-gray-600">
                        {formatDateLabel(entry.date)}
                      </td>
                      <td className="px-5 py-2.5 font-semibold text-gray-900">
                        {entry.employeeId?.name || entry.employeeName || "—"}
                      </td>
                      <td className="px-5 py-2.5 text-gray-600">
                        {formatRoleLabel(entry.employeeId?.role || entry.employeeRole)}
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-900">
                        {formatCurrency(entry.amount)}
                      </td>
                      <td className="px-5 py-2.5 text-gray-600">
                        {entry.remarks || "—"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProjectExpensesReport;
